import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import VendorRegistration from "../components/VendorRegistration";
import Footer from "../components/Footer";
import "./RegistrationPage.css";

const RegistrationPage = () => {
  const { auth, logout } = useAuth();
  // Create refs for the sections
  const registrationRef = useRef(null);
  const infoRef = useRef(null);
  const targetAudienceRef = useRef(null);
  const testimonialRef = useRef(null);
  const brandsRef = useRef(null);

  // State for testimonials rotation
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [visibleBrands, setVisibleBrands] = useState([0, 1, 2, 3, 4]);

  // If user is logged in, log them out
  useEffect(() => {
    if (auth) {
      logout();
    }
  }, [auth, logout]);

  // Function to handle scrolling to the registration component
  const scrollToRegistration = () => {
    if (registrationRef.current) {
      registrationRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Setup intersection observers for scroll animations
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    
    // Observe all sections with animation
    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % 4);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Rotate brand logos every 3 seconds in banner style
  useEffect(() => {
    const totalBrands = 10;
    // Show fewer brands in the banner - just 5 at a time for a cleaner look
    const visibleCount = 5;
    
    const interval = setInterval(() => {
      setVisibleBrands(prev => {
        const newVisible = [...prev];
        newVisible.shift();
        const nextBrand = (newVisible[newVisible.length - 1] + 1) % totalBrands;
        newVisible.push(nextBrand);
        return newVisible;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Brand logos array with image URLs
  const brands = [
    {
      name: "Green Leaf",
      imageUrl: "/images/brands/greenleaf-logo.png" // These paths would point to your public/images folder
    },
    {
      name: "Herbal Solutions",
      imageUrl: "/images/brands/herbalsolutions-logo.png"
    },
    {
      name: "Canna Health",
      imageUrl: "/images/brands/cannahealth-logo.png"
    },
    {
      name: "MediGreen",
      imageUrl: "/images/brands/medigreen-logo.png"
    },
    {
      name: "Pure Relief",
      imageUrl: "/images/brands/purerelief-logo.png"
    },
    {
      name: "Nature's Way",
      imageUrl: "/images/brands/naturesway-logo.png"
    },
    {
      name: "Wellness CBD",
      imageUrl: "/images/brands/wellnesscbd-logo.png"
    },
    {
      name: "Tranquil Farm",
      imageUrl: "/images/brands/tranquilfarm-logo.png"
    },
    {
      name: "Zen Garden",
      imageUrl: "/images/brands/zengarden-logo.png"
    },
    {
      name: "Elevation",
      imageUrl: "/images/brands/elevation-logo.png"
    }
  ];

  return (
    <div className="registration-page">
      {/* Upper Section */}
      <div className="logo-container">
        <a href="https://www.weedhurry.com">
          <img
            src={require('../assets/images/addressPageLogo.png')}
            alt="WeedHurry Logo"
            className="logo"
          />
        </a>
      </div>

      {/* Hero Section */}
      <div className="hero-section animate-on-scroll">
        <div className="hero-content">
          <h1 className="hero-title">Become a Weedhurry Vendor Today</h1>
          <p className="hero-subtitle">Join our growing network of retailers and reach more customers</p>
          <div className="pricing-container">
            <span className="subscription-text">Monthly Subscription</span>
            <span className="pricing-crossed">$79 per month</span>
            <div className="free-trial-container">
              <span className="pricing-free">FREE TRIAL</span>
            </div>
            <span className="pricing-period">Sign up today for 60 days free access</span>
          </div>
          <button 
            className="hero-button"
            onClick={scrollToRegistration}
          >
            Start Your Free Trial
          </button>
        </div>
      </div>

      {/* Info Section */}
      <div className="info-section container" ref={infoRef}>
        <h2 className="section-title animate-on-scroll">Why join Weedhurry?</h2>
        
        <div className="features-container">
          <div className="feature-card animate-on-scroll" style={{animationDelay: "0.1s"}}>
            <div className="feature-icon">
              <i className="fas fa-map-marker-alt"></i>
              <span className="feature-emoji">📍</span>
            </div>
            <h4>Location-Based Orders</h4>
            <p>Easily set and update your preferred area for deliveries.</p>
          </div>
          
          <div className="feature-card animate-on-scroll" style={{animationDelay: "0.2s"}}>
            <div className="feature-icon">
              <i className="fas fa-tasks"></i>
              <span className="feature-emoji">💬</span>
            </div>
            <h4>Text Notifications</h4>
            <p>Save multiple phone numbers for new order text notifications.</p>
          </div>
          
          <div className="feature-card animate-on-scroll" style={{animationDelay: "0.3s"}}>
            <div className="feature-icon">
              <i className="fas fa-store"></i>
              <span className="feature-emoji">⚙️</span>
            </div>
            <h4>Store Control</h4>
            <p>Upload your logo, set business hours and create your menu from our intuitive dashboard.</p>
          </div>
          
          <div className="feature-card animate-on-scroll" style={{animationDelay: "0.4s"}}>
            <div className="feature-icon">
              <i className="fas fa-motorcycle"></i>
              <span className="feature-emoji">🚚</span>
            </div>
            <h4>Courier Service</h4>
            <p>Access our reliable courier network for a flat commission on each order.</p>
          </div>
        </div>
      </div>

      {/* New Target Audience Section */}
      <div className="target-audience-section" ref={targetAudienceRef}>
        <div className="container">
          <div className="audience-content animate-on-scroll">
            <div className="audience-text">
              <h2 className="section-title">Tap Into the College Market</h2>
              <p className="audience-description">
                Weedhurry has built relationships with different cannabis clubs at universities and colleges across the state, 
                helping you tap into this valuable demographic.
              </p>
              <p className="audience-description">
                Our print advertising campaigns put your business in front of tech-savy consumers who value convenience and are eager to spread the word.
              </p>
            </div>
            <div className="audience-image animate-on-scroll" style={{animationDelay: "0.2s"}}>
              <img
                src="https://ca-times.brightspotcdn.com/dims4/default/677c47d/2147483647/strip/true/crop/6258x4172+0+0/resize/1200x800!/quality/75/?url=https%3A%2F%2Fcalifornia-times-brightspot.s3.amazonaws.com%2Ff1%2Fbc%2Fb8161e774f82becba07d9d2295a1%2F1297686-me-uc-ucla-campus-jlc-0517-10722.jpg"
                alt="College campus"
                className="campus-image"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section with Rotation */}
      <div className="testimonial-section" ref={testimonialRef}>
        <div className="container">
          <h2 className="section-title animate-on-scroll">What Our Vendors Say</h2>
          <div className="testimonials-container animate-on-scroll">
            {/* Testimonial 1 */}
            <div className={`testimonial-card ${activeTestimonial === 0 ? 'active' : ''}`}>
              <div className="testimonial-content">
                <i className="fas fa-quote-left testimonial-quote-icon"></i>
                <p className="testimonial-text">
                  "weedhurry is great, it was easy to upload our menu and we got orders from UCLA area on the first day! 🔥🔥!"
                </p>
                <div className="testimonial-author">
                  <h5>Tyler S.</h5>
                  <p>Owner of Jade Leaf, Los Angeles</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className={`testimonial-card ${activeTestimonial === 1 ? 'active' : ''}`}>
              <div className="testimonial-content">
                <i className="fas fa-quote-left testimonial-quote-icon"></i>
                <p className="testimonial-text">
                  "Our delivery radius used to be so limited, but with weedhurry we've expanded our customer base. Our weekend sales are up 100%!"
                </p>
                <div className="testimonial-author">
                  <h5>Maria J.</h5>
                  <p>Operations Manager, Green Express, San Diego</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className={`testimonial-card ${activeTestimonial === 2 ? 'active' : ''}`}>
              <div className="testimonial-content">
                <i className="fas fa-quote-left testimonial-quote-icon"></i>
                <p className="testimonial-text">
                  "I appreciate the simple dashboard and order management system. It's easy to use by me and my staff and has helped us get new customers."
                </p>
                <div className="testimonial-author">
                  <h5>David L.</h5>
                  <p>Founder of Cosmic Greens, Berkeley</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className={`testimonial-card ${activeTestimonial === 3 ? 'active' : ''}`}>
              <div className="testimonial-content">
                <i className="fas fa-quote-left testimonial-quote-icon"></i>
                <p className="testimonial-text">
                  "We joined weedhurry six months ago and the college student demographic has become our most loyal customer base. The platform practically markets itself!"
                </p>
                <div className="testimonial-author">
                  <h5>Rebecca T.</h5>
                  <p>Marketing Director, Pure Bloom, Sacramento</p>
                </div>
              </div>
            </div>

            <div className="testimonial-indicators">
              <span 
                className={`indicator ${activeTestimonial === 0 ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(0)}
              ></span>
              <span 
                className={`indicator ${activeTestimonial === 1 ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(1)}
              ></span>
              <span 
                className={`indicator ${activeTestimonial === 2 ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(2)}
              ></span>
              <span 
                className={`indicator ${activeTestimonial === 3 ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(3)}
              ></span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section - Registration */}
      <div 
        ref={registrationRef} 
        className="registration-container animate-on-scroll"
      >
        <div className="registration-header">
          <h2 className="section-title">Start Selling Today</h2>
          <p className="registration-subtitle">
            Fill out the form below to gain access to the vendor dashboard where you can upload your menu and setup your store on the Weedhurry marketplace.
          </p>
        </div>
        <VendorRegistration />
      </div>
      <Footer />
    </div>
  );
};

export default RegistrationPage;